import React, { useState, useContext, useEffect, useLayoutEffect, memo } from 'react';
import {
  IonPage,
  IonContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSearchbar,
  IonList,
  IonItem,
  IonAvatar,
  IonBadge,
  IonText,
  IonAccordionGroup,
  IonAccordion,
  IonDatetime,
  IonButton,
  IonIcon,
  IonSpinner,
  IonModal,
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { calendarOutline } from 'ionicons/icons';
import { ProfileContext } from '../../context/ProfileContext';
import { WerkerSchedulerHeader } from '../../components/WerkHeader';
import { LoadingScreen } from '../../components/LoadingScreen';
import { HEADERTEXT_TYPES } from '../../functions/postActionTypes';
import { isDesktop } from '../../utils/platformUtils';
import PaymentsDesktop from '../desktop/PaymentsDesktop';
import { 
  getUnpaidWerkersForScheduler,
  getPaymentReceipts,
} from '../../functions/Fx_Payment';
import {
  UnpaidWerkerSummary,
  WerkerPaymentHistory,
  PaymentReceipt,
} from '../../types/payment';
import {
  formatCurrency,
  formatDate,
  getCurrentYearStart,
  getCurrentTimestamp,
} from '../../utils/paymentCalculations';
import { JobSelectionModal } from '../../components/payments/JobSelectionModal';
import '../../theme/AppStyles.css';

type PaymentSegment = 'unpaid' | 'history';

interface PaymentsLocationState {
  segment?: PaymentSegment;
  refresh?: boolean;
}

const PaymentsMobile: React.FC = memo(() => {
  const history = useHistory();
  const location = useLocation<PaymentsLocationState>();
  const { profile, colorTheme } = useContext(ProfileContext);

  const [activeSegment, setActiveSegment] = useState<PaymentSegment>(
    location.state?.segment || 'unpaid'
  );
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStartTime, setLoadingStartTime] = useState<number>(Date.now());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0); // Start at 0, will be set by pathname effect

  // Always refresh when navigating to Payments page (all navigation methods)
  // Use useLayoutEffect to run synchronously before paint, preventing flash of stale data
  useLayoutEffect(() => {
    if (location.pathname === '/payments') {
      setIsLoading(true);
      setLoadingStartTime(Date.now());
      setLastRefreshTime(Date.now());
      setDateRangeEnd(getCurrentTimestamp());
      
      // Handle segment from location state (e.g., Done button → History)
      if (location.state?.segment) {
        setActiveSegment(location.state.segment);
      }
    }
  }, [location.pathname, location.state?.segment]);

  // Unpaid segment state
  const [unpaidWerkers, setUnpaidWerkers] = useState<UnpaidWerkerSummary[]>([]);
  const [selectedWerker, setSelectedWerker] = useState<UnpaidWerkerSummary | null>(null);
  const [isJobSelectionModalOpen, setIsJobSelectionModalOpen] = useState(false);

  // History segment state
  const [paymentHistory, setPaymentHistory] = useState<WerkerPaymentHistory[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState<number>(getCurrentYearStart());
  const [dateRangeEnd, setDateRangeEnd] = useState<number>(getCurrentTimestamp());
  const [activeDatePicker, setActiveDatePicker] = useState<'start' | 'end' | null>(null);

  // Format a timestamp as a local YYYY-MM-DD string for IonDatetime (avoids UTC timezone double-highlight)
  const toLocalDateValue = (ts: number): string => {
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Parse a YYYY-MM-DD string from IonDatetime as local noon (avoids off-by-one from UTC midnight)
  const fromLocalDateValue = (val: string): number => {
    const [y, m, d] = val.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).getTime();
  };

  // Helper to enforce minimum 1 second loading display time
  const finishLoading = () => {
    const elapsed = Date.now() - loadingStartTime;
    const remaining = Math.max(0, 1000 - elapsed);
    setTimeout(() => setIsLoading(false), remaining);
  };

  // Load unpaid werkers
  useEffect(() => {
    // Don't run on initial mount (lastRefreshTime = 0), wait for pathname effect to set it
    if (!profile.UserId || lastRefreshTime === 0) {
      return;
    }

    const loadUnpaidWerkers = async () => {
      try {
        setIsLoading(true);
        setLoadingStartTime(Date.now());
        
        // Add delay if payment just completed to allow Firestore index propagation
        const queryDelay = sessionStorage.getItem('paymentJustCompleted') === 'true' ? 500 : 0;
        sessionStorage.removeItem('paymentJustCompleted');
        
        await new Promise(resolve => setTimeout(resolve, queryDelay));
        
        const werkers = await getUnpaidWerkersForScheduler(profile.UserId);
        // Simple: always use what Firestore returns (no optimistic filtering)
        setUnpaidWerkers(werkers);
      } catch (error) {
        console.error('Error loading unpaid werkers:', error);
      } finally {
        finishLoading();
        setIsInitialLoad(false);
      }
    };

    if (activeSegment === 'unpaid') {
      loadUnpaidWerkers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.UserId, activeSegment, lastRefreshTime]);

  // Sync selectedWerker with filtered unpaidWerkers after optimistic filtering
  // Ensures modal shows current data after partial payments
  useEffect(() => {
    if (selectedWerker && isJobSelectionModalOpen) {
      const updated = unpaidWerkers.find(w => w.werkerUid === selectedWerker.werkerUid);
      if (updated && updated !== selectedWerker) {
        // Werker still has unpaid jobs - update reference to filtered object
        setSelectedWerker(updated);
      } else if (!updated) {
        // All jobs were paid - close modal gracefully
        setSelectedWerker(null);
        setIsJobSelectionModalOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unpaidWerkers, isJobSelectionModalOpen]);

  // Keep unpaid badge count fresh after a payment, even when on the history segment
  useEffect(() => {
    if (!profile.UserId || activeSegment === 'unpaid') return;
    getUnpaidWerkersForScheduler(profile.UserId)
      .then(werkers => {
        // Simple: always use what Firestore returns
        setUnpaidWerkers(werkers);
      })
      .catch(() => {});
  }, [profile.UserId, lastRefreshTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load payment history
  useEffect(() => {
    // Don't run on initial mount (lastRefreshTime = 0), wait for pathname effect to set it
    if (!profile.UserId || lastRefreshTime === 0) {
      return;
    }

    const loadPaymentHistory = async () => {
      try {
        setIsLoading(true);
        setLoadingStartTime(Date.now());
        const history = await getPaymentReceipts(profile.UserId, {
          startDate: dateRangeStart,
          endDate: dateRangeEnd,
        });
        setPaymentHistory(history);
      } catch (error) {
        console.error('Error loading payment history:', error);
      } finally {
        finishLoading();
        setIsInitialLoad(false);
      }
    };

    if (activeSegment === 'history') {
      loadPaymentHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.UserId, activeSegment, dateRangeStart, dateRangeEnd, lastRefreshTime]);

  const handleSegmentChange = (segment: PaymentSegment) => {
    setActiveSegment(segment);
    setSearchText('');
  };

  const handleWerkerClick = (werker: UnpaidWerkerSummary) => {
    setSelectedWerker(werker);
    setIsJobSelectionModalOpen(true);
  };

  const handleJobSelectionDismiss = async (data?: any) => {
    setIsJobSelectionModalOpen(false);
    
    if (data && data.selectedJobs) {
      // Navigate to payment summary page
      history.push({
        pathname: '/payments/summary',
        state: {
          werkerData: selectedWerker,
          selectedJobs: data.selectedJobs,
        },
      });
    }
    
    setSelectedWerker(null);
  };

  const handleReceiptClick = (receipt: PaymentReceipt) => {
    history.push(`/payments/receipt/${receipt.receiptId}`);
  };

  // Filter werkers/history by search text
  const filteredUnpaidWerkers = unpaidWerkers.filter((werker) =>
    werker.werkerName.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredPaymentHistory = paymentHistory.filter((werker) =>
    werker.werkerName.toLowerCase().includes(searchText.toLowerCase())
  );

  const totalUnpaidDisplay = unpaidWerkers.reduce((sum, w) => sum + w.totalUnpaid, 0);
  const totalPaidDisplay = paymentHistory.reduce((sum, w) => sum + w.totalPaid, 0);

  // Show loading screen only on initial page load — keep IonPage mounted so Ionic
  // can drive the route transition cleanly (prevents searchbar/header overlap on iOS).
  // The `bckgrnd` class makes IonPage opaque during loading so the previous page
  // doesn't bleed through the transparent LoadingScreen during the slide.
  const showInitialLoading = isInitialLoad && isLoading;

  return (
    <IonPage className={showInitialLoading ? "bckgrnd" : ""}>
      {showInitialLoading ? (
        <LoadingScreen LoadingText="Loading payments..." />
      ) : (
        <>
      {/* Pass segment + searchbar as `subheader` so they render INSIDE
          <IonHeader> and slide with the page on iOS. Bare <div> siblings
          between IonHeader and IonContent get left behind by Ionic's iOS
          slide transition. */}
      <WerkerSchedulerHeader
        title={HEADERTEXT_TYPES.Payments}
        subheader={
          <>
            {/* Segment Selector */}
            <div style={{ paddingTop: 10, paddingBottom: 4, background: 'var(--ion-background-color, #fff)' }}>
              <IonSegment
                className={colorTheme}
                value={activeSegment}
                onIonChange={(e: any) => handleSegmentChange(e.detail.value!)}
                style={{ width: '100%' }}
              >
                <IonSegmentButton value="unpaid">
                  <IonLabel>
                    <h2>
                      Unpaid{' '}
                      {unpaidWerkers.length > 0 && (
                        <IonBadge color="danger">{unpaidWerkers.length}</IonBadge>
                      )}
                    </h2>
                  </IonLabel>
                </IonSegmentButton>

                <IonSegmentButton value="history">
                  <IonLabel>
                    <h2>History</h2>
                  </IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </div>

            {/* Search bar */}
            <div style={{ background: 'var(--ion-background-color, #fff)' }}>
              <IonSearchbar
                value={searchText}
                onIonChange={(e) => setSearchText(e.detail.value!)}
                placeholder={activeSegment === 'unpaid' ? 'Search werkers...' : 'Search payment history...'}
                animated
              />
            </div>

            {/* Date range for history segment */}
            {activeSegment === 'history' && (
              <div style={{ background: 'var(--ion-background-color, #fff)' }}>
                <div style={{ padding: '0 16px 8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <IonButton
                    fill="outline"
                    size="small"
                    onClick={() => setActiveDatePicker('start')}
                  >
                    <IonIcon slot="start" icon={calendarOutline} />
                    Start: {formatDate(dateRangeStart)}
                  </IonButton>
                  <IonButton
                    fill="outline"
                    size="small"
                    onClick={() => setActiveDatePicker('end')}
                  >
                    <IonIcon slot="start" icon={calendarOutline} />
                    End: {formatDate(dateRangeEnd)}
                  </IonButton>
                </div>
              </div>
            )}
          </>
        }
      />

      <IonContent scrollX={false} scrollY={true}>
        {/* Show inline spinner when loading after segment switch */}
        {!isInitialLoad && isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <>
        {/* Unpaid Segment Content */}
        {activeSegment === 'unpaid' && (
          <>
            {filteredUnpaidWerkers.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <IonText color="medium">
                  <h3>All caught up!</h3>
                  <p>No unpaid jobs at this time.</p>
                </IonText>
              </div>
            ) : (
              <>
                <div style={{ padding: '8px 16px' }}>
                  <IonText color="medium">
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      Total Unpaid: <strong>{formatCurrency(totalUnpaidDisplay)}</strong>
                    </p>
                  </IonText>
                </div>

                <IonList>
                  {filteredUnpaidWerkers.map((werker) => (
                    <IonItem
                      key={werker.werkerUid}
                      button
                      onClick={() => handleWerkerClick(werker)}
                    >
                      <IonAvatar slot="start">
                        <img
                          src={werker.werkerProfilePic || '/assets/profilePic.png'}
                          alt={werker.werkerName}
                        />
                      </IonAvatar>
                      <IonLabel>
                        <h2>{werker.werkerName}</h2>
                        <p>{werker.jobCount} job{werker.jobCount !== 1 ? 's' : ''}</p>
                      </IonLabel>
                      <IonText slot="end" color="primary">
                        <strong>{formatCurrency(werker.totalUnpaid)}</strong>
                      </IonText>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </>
        )}

        {/* History Segment Content */}
        {activeSegment === 'history' && (
          <>
            {filteredPaymentHistory.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <IonText color="medium">
                  <h3>No payment history</h3>
                  <p>No payments found in this date range.</p>
                </IonText>
              </div>
            ) : (
              <>
                <div style={{ padding: '8px 16px' }}>
                  <IonText color="medium">
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      Total Paid: <strong>{formatCurrency(totalPaidDisplay)}</strong>
                    </p>
                  </IonText>
                </div>

                <IonAccordionGroup>
                  {filteredPaymentHistory.map((werkerHistory) => (
                    <IonAccordion key={werkerHistory.werkerUid} value={werkerHistory.werkerUid}>
                      <IonItem slot="header">
                        <IonAvatar slot="start">
                          <img
                            src={werkerHistory.werkerProfilePic || '/assets/profilePic.png'}
                            alt={werkerHistory.werkerName}
                          />
                        </IonAvatar>
                        <IonLabel>
                          <h2>{werkerHistory.werkerName}</h2>
                          <p>{werkerHistory.receiptCount} payment{werkerHistory.receiptCount !== 1 ? 's' : ''}</p>
                        </IonLabel>
                        <IonText slot="end" color="success">
                          <strong>{formatCurrency(werkerHistory.totalPaid)}</strong>
                        </IonText>
                      </IonItem>

                      <IonList slot="content">
                        {werkerHistory.receipts.map((receipt) => (
                          <IonItem
                            key={receipt.receiptId}
                            button
                            onClick={() => handleReceiptClick(receipt)}
                          >
                            <IonLabel>
                              <h3>{formatDate(receipt.paymentDate)}</h3>
                              <p>{receipt.jobs.length} job{receipt.jobs.length !== 1 ? 's' : ''}</p>
                            </IonLabel>
                            <IonText slot="end" color="dark">
                              {formatCurrency(receipt.totalAmount)}
                            </IonText>
                          </IonItem>
                        ))}
                      </IonList>
                    </IonAccordion>
                  ))}
                </IonAccordionGroup>
              </>
            )}
          </>
        )}
          </>
        )}
      </IonContent>

      {/* Job Selection Modal */}
      {selectedWerker && (
        <JobSelectionModal
          isOpen={isJobSelectionModalOpen}
          werkerData={selectedWerker}
          onDismiss={handleJobSelectionDismiss}
        />
      )}

      {/* Start Date Picker Modal */}
      <IonModal
        isOpen={activeDatePicker === 'start'}
        onDidDismiss={() => setActiveDatePicker(null)}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
      >
        <IonContent>
          <div style={{ padding: '16px' }}>
            <IonText><h3>Select Start Date</h3></IonText>
            <IonDatetime
              presentation="date"
              value={toLocalDateValue(dateRangeStart)}
              onIonChange={(e: any) => {
                if (e.detail.value) {
                  setDateRangeStart(fromLocalDateValue(e.detail.value));
                }
              }}
              className="payments-datetime"
              style={{
                width: '100%',
                '--ion-color-primary': '#ff7900',
                '--ion-color-primary-rgb': '255, 121, 0',
                '--ion-color-primary-contrast': '#000000',
                '--ion-color-primary-contrast-rgb': '0, 0, 0',
                '--ion-color-primary-shade': '#cc6100',
                '--ion-color-primary-tint': '#ff8c1a',
              } as any}
            />
            <button
              onClick={() => setActiveDatePicker(null)}
              style={{
                marginTop: '16px',
                width: '100%',
                backgroundColor: '#ff7900',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        </IonContent>
      </IonModal>

      {/* End Date Picker Modal */}
      <IonModal
        isOpen={activeDatePicker === 'end'}
        onDidDismiss={() => setActiveDatePicker(null)}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
      >
        <IonContent>
          <div style={{ padding: '16px' }}>
            <IonText><h3>Select End Date</h3></IonText>
            <IonDatetime
              presentation="date"
              value={toLocalDateValue(dateRangeEnd)}
              onIonChange={(e: any) => {
                if (e.detail.value) {
                  setDateRangeEnd(fromLocalDateValue(e.detail.value));
                }
              }}
              className="payments-datetime"
              style={{
                width: '100%',
                '--ion-color-primary': '#ff7900',
                '--ion-color-primary-rgb': '255, 121, 0',
                '--ion-color-primary-contrast': '#000000',
                '--ion-color-primary-contrast-rgb': '0, 0, 0',
                '--ion-color-primary-shade': '#cc6100',
                '--ion-color-primary-tint': '#ff8c1a',
              } as any}
            />
            <button
              onClick={() => setActiveDatePicker(null)}
              style={{
                marginTop: '16px',
                width: '100%',
                backgroundColor: '#ff7900',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        </IonContent>
      </IonModal>
        </>
      )}
    </IonPage>
  );
});

const PaymentsPage: React.FC = () => {
  if (isDesktop()) return <PaymentsDesktop />;
  return <PaymentsMobile />;
};

export default PaymentsPage;
